/**
 * Scoped network/field/reservoir output views fronting the four remaining
 * un-fronted structured output sources:
 *   - `JointOutputs_Network_Agent`     → `JointOutputs`     (NETWORK grain)
 *   - `SeparatorOutputs_Network_Agent` → `SeparatorOutputs` (NETWORK grain)
 *   - `FieldOutputs_Agent`             → `FieldOutputs`     (FIELD grain)
 *   - `ReservoirOutputs_Agent`         → `ReservoirOutputs` (RESERVOIR grain)
 *
 * The Joint/Separator sources are per-run network-solve outputs — their measures
 * are per-row timeseries handles — so each is a timestamp-aligned surface:
 * `createAlignedSeriesView` emits one row per (source row × run timestamp) with
 * each measure as its own typed column (its unit carried as column-type metadata)
 * plus a `timestamp` column, broadcasting identity + the grain relation onto every
 * aligned row. Those close over the per-session CDF client, so the producer is a
 * hook.
 *
 * The Field/Reservoir sources are scalar per-grain surfaces (well-activity
 * counts, VRR metrics), kept as pure `createReshapedView` projections — one
 * de-prefixed view per source, keeping identity + the grain's direct-relation(s)
 * and de-marking the measure family with `suffix === source column` (no rename).
 *
 * NOTE the grain refs are authored verbatim rather than reusing the daily-rollup
 * relation shapes: Joint/Separator name their grain ref `network` (not the
 * NetworkInputs `networkName`) and Reservoir names it `reservoir` (not the
 * rollup `reservoirEntity`). Field's grain ref IS the rollup `fieldEntity`, so
 * `FIELD_RELATION` is reused there. Joint/Separator's `entityName` is a
 * `z.json()` blob (→ `unknownCol`) and is excluded from the kept relations.
 */

import { createAlignedSeriesView } from '../createAlignedSeriesView.ts';
import { createReshapedView, type VirtualView } from '../createReshapedView.ts';
import {
  FIELD_RELATION,
  FIELD_WELL_COUNTS,
  IDENTITY,
  JOINT_OUTPUTS,
  JOINT_OUTPUTS_RELATION,
  RESERVOIR_OUTPUTS_RELATION,
  RESERVOIR_VRR,
  SEPARATOR_OUTPUTS,
  SEPARATOR_OUTPUTS_RELATION,
  WELL_COUNTS_CORE,
} from '../shapes/index.ts';
import { useCatalogEnrichContext } from './useCatalogEnrichContext.ts';
import {
  FieldOutputsAgentView,
  JointOutputsNetworkAgentView,
  ReservoirOutputsAgentView,
  SeparatorOutputsNetworkAgentView,
} from '#schema/generated/ipso/views.ts';

/**
 * Build the four scoped-output surfaces fronting the network joint/separator and
 * field/reservoir output sources: the Joint/Separator aligned-series surfaces
 * (one typed column per measure with its unit + a `timestamp` column, closing
 * over the per-session CDF client) plus the scalar Field/Reservoir reshape
 * views. Each keeps identity + the grain relation(s).
 */
export const useScopedOutputsViews = (): readonly VirtualView[] => {
  const { client, mintSeries } = useCatalogEnrichContext();
  return [
    createAlignedSeriesView({
      name: 'JointOutputs',
      description:
        'Junction/node-level outputs from the GAP network simulation (NETWORK grain): one row per (junction × run timestamp) with each measure as its own typed column, covering oil/gas/water + liquid rates, gas/water injection rates, operating pressure/temperature, contaminant fractions (CO2/N2/H2S), fluid ratios (GOR/CGR/WGR/GLR/water cut), downstream conditions, and the violating-constraints flag. Filter by network (or join the network entity) to scope to a network; jointEntity references the junction/manifold.',
      source: JointOutputsNetworkAgentView,
      keep: [...IDENTITY, ...JOINT_OUTPUTS_RELATION],
      groups: { shape: JOINT_OUTPUTS },
      client,
      mintSeries,
    }),
    createAlignedSeriesView({
      name: 'SeparatorOutputs',
      description:
        'Separator-level outputs from the GAP network simulation (NETWORK grain): one row per (separator × run timestamp) with each measure as its own typed column, covering simulated and measured oil/gas/water rates, total liquid rate, operating pressure/temperature, fluid ratios (GOR/CGR/WGR/GLR/water cut), facility-max-intake utilization percentages, and the violating/limiting-constraint flags. Filter by network (or join the network entity) to scope to a network; separatorEntity references the separator.',
      source: SeparatorOutputsNetworkAgentView,
      keep: [...IDENTITY, ...SEPARATOR_OUTPUTS_RELATION],
      groups: { shape: SEPARATOR_OUTPUTS },
      client,
      mintSeries,
    }),
    createReshapedView({
      name: 'FieldOutputs',
      description:
        'Field-level well-activity counts from the GAP network model (FIELD grain): counts of active and inactive wells by production / lift / injection mode (natural-flow oil & gas producers, ESP- and gas-lift oil producers, gas/water injectors, and water-alternating-gas injectors). For field-level scenario/optimization rate totals see the NetworkOutputs_Field views. Filter by fieldEntity (or join the field entity) to scope to a field.',
      source: FieldOutputsAgentView,
      keep: [...IDENTITY, ...FIELD_RELATION],
      groups: [{ shape: FIELD_WELL_COUNTS }],
      client,
      mintSeries,
    }),
    createReshapedView({
      name: 'ReservoirOutputs',
      description:
        'Reservoir-level VRR metrics and injection balance (RESERVOIR grain): daily/MTD/YTD voidage-replacement ratios and their compliance flags, region pressure and pressure compliance, gas/water injection actual vs target rates and their compliance flags, downhole production/injection volumes, plus active/inactive well-activity counts. Filter by reservoir (or join the reservoir entity) to scope to a reservoir; field references the parent field.',
      source: ReservoirOutputsAgentView,
      keep: [...IDENTITY, ...RESERVOIR_OUTPUTS_RELATION],
      groups: [{ shape: RESERVOIR_VRR }, { shape: WELL_COUNTS_CORE }],
      client,
      mintSeries,
    }),
  ];
};
